import { useState } from 'react';

import { tss } from 'tss-react/mui';

import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  AccountCircleOutlined,
  ExpandMore,
  ExitToAppOutlined,
  HelpOutlineOutlined,
  MenuOutlined,
  MenuOpenOutlined,
} from '@mui/icons-material';

import { useRouter } from 'next/navigation';

import { env } from '@/env';
import { or2UserAvatarImageLoader } from '@/modules/image/loaders/or2ImageLoader';
import { NotificationsIndicator } from '@/components/ServiceWorker/NotificationsIndicator';

import { Or2Image } from '../image/Or2Image';
import { Or2Button } from '../button/Or2Button';
import { Or2Avatar } from '../Avatar/Or2Avatar';
import { LaunchAssistantButton } from '../../modules/assistant/components/LaunchAssistantButton';

const useStyles = tss.create(({ theme }) => ({
  commonMenu: {
    height: '58px',
    position: 'sticky' /* Set the navbar to fixed position */,
    top: '0' /* Position the navbar at the top of the page */,
    width: '100%' /* Full width */,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
  },
  commonMenuInner: {
    display: 'flex',
    alignItems: 'center',
    flexBasis: '100%',
  },
  menuStart: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.background,
    paddingLeft: theme.spacing(2),
  },
  spacer: {
    flexGrow: 1,
    backgroundColor: theme.background,
    borderBottomRightRadius: theme.shape.borderRadius,
    height: '100%',
  },
  commonMenuItems: {
    display: 'flex',
    backgroundColor: theme.palette.common.white,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(1),
    borderTop: `1px solid ${theme.cardBorder}`,
    paddingTop: '6px', // offset borders
    borderRight: `1px solid ${theme.cardBorder}`,
    paddingBottom: theme.spacing(0.5),
  },
  commonMenuItemsWrapper: {
    height: '100%',
    backgroundColor: theme.background,
  },
  avatar: {
    width: '32px',
    height: '32px',
  },
  menuButton: {
    color: '#12263F',
    marginLeft: theme.spacing(1),
    height: '100%',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}));

interface MenuUser {
  _id: string | null | undefined;
  baseImage: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
}

interface Props {
  user: MenuUser | null | undefined;
  loading: boolean;
  smallScreenDrawerOpen: boolean;
  largeScreenDrawerOpen: boolean;
  onSignout: () => void;
  onToggleDrawer: () => void;
}

export const CommonHeader = ({
  user,
  loading,
  smallScreenDrawerOpen,
  largeScreenDrawerOpen,
  onSignout,
  onToggleDrawer,
}: Props) => {
  const theme = useTheme();
  const { classes, cx } = useStyles();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const router = useRouter();
  const [signoutLoading, setSignoutLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const { _id: userId, firstName, lastName, baseImage } = user || {};

  const nameString = firstName && lastName ? `${firstName} ${lastName}` : null;

  const handleOnSignout = async () => {
    setSignoutLoading(true);
    onSignout();
  };

  const handleUserClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserClose = () => {
    setAnchorEl(null);
  };

  const handleProfileLink = () => {
    router.push(`/user/${userId}`);
    handleUserClose();
  };

  return (
    <div className={classes.commonMenu}>
      <div className={classes.commonMenuInner}>
        <div className={classes.menuStart}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onToggleDrawer}
            sx={{ mr: 1 }}
          >
            <MenuOpenOutlined
              sx={[
                smallScreenDrawerOpen
                  ? {
                      display: {
                        xs: 'block',
                      },
                    }
                  : {
                      display: {
                        xs: 'none',
                      },
                    },
                largeScreenDrawerOpen
                  ? {
                      display: {
                        md: 'block',
                      },
                    }
                  : {
                      display: {
                        md: 'none',
                      },
                    },
              ]}
            />
            <MenuOutlined
              sx={[
                smallScreenDrawerOpen
                  ? {
                      display: {
                        xs: 'none',
                      },
                    }
                  : {
                      display: {
                        xs: 'block',
                      },
                    },
                largeScreenDrawerOpen
                  ? {
                      display: {
                        md: 'none',
                      },
                    }
                  : {
                      display: {
                        md: 'block',
                      },
                    },
              ]}
            />
          </IconButton>
          <LaunchAssistantButton />
          <span style={{ marginLeft: 8 }}>
            <NotificationsIndicator />
          </span>
        </div>

        <div className={classes.spacer} />
        <div className={classes.commonMenuItemsWrapper}>
          <div className={classes.commonMenuItems}>
            <Or2Button
              onClick={handleUserClick}
              aria-controls="user-account-button"
              aria-haspopup="true"
              size="small"
              color="inherit"
              variant="text"
              className={classes.menuButton}
              disabled={loading}
              startIcon={
                loading ? (
                  <Skeleton
                    variant="circular"
                    animation="wave"
                    width={32}
                    height={32}
                  />
                ) : baseImage ? (
                  <Or2Avatar className={classes.avatar} variant="circular">
                    <Or2Image
                      src={baseImage}
                      alt={`${nameString ?? 'User'} image`}
                      loading={loading}
                      size={32}
                      loader={or2UserAvatarImageLoader}
                    />
                  </Or2Avatar>
                ) : undefined
              }
              endIcon={
                <ExpandMore
                  className={cx(classes.expand, {
                    [classes.expandOpen]: Boolean(anchorEl),
                  })}
                />
              }
            >
              {smallScreen ? (
                ''
              ) : loading ? (
                <Skeleton variant="text" width={75} />
              ) : (
                nameString
              )}
            </Or2Button>

            <Menu
              id="user-account-button"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleUserClose}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <MenuItem dense onClick={handleProfileLink}>
                <ListItemIcon>
                  <AccountCircleOutlined color="primary" />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </MenuItem>

              <MenuItem
                dense
                component="a"
                href={env.NEXT_PUBLIC_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleUserClose}
              >
                <ListItemIcon>
                  <HelpOutlineOutlined color="primary" />
                </ListItemIcon>
                <ListItemText primary="Help" />
              </MenuItem>

              <MenuItem
                dense
                disabled={signoutLoading}
                onClick={handleOnSignout}
              >
                <ListItemIcon>
                  {signoutLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <ExitToAppOutlined color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={signoutLoading ? 'Logging out...' : 'Logout'}
                />
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};
